import React from "react";
import { Outlet, Navigate, useLocation, Link } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import NotificationBell from "@/components/NotificationBell";
import FormatToggle from "@/components/FormatToggle";
import MasterRefresh from "@/components/MasterRefresh";
import TradeTicker from "@/components/TradeTicker";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, LayoutGrid } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";

export default function Layout() {
  const { user, access, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
        <Loader2 className="w-6 h-6 spin text-[var(--accent)]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  const isPro = !!access?.has_access;
  // The player-detail page lists every per-format value explicitly, and the Command Center
  // aggregates leagues that each keep their own format — so the global format toggle is a
  // no-op on both. Hide it there to avoid a misleading control.
  // The global VALUE FORMAT toggle only drives pages that read it without their own control.
  // It's a no-op / confusing on the dashboard (per-league formats), the player-detail page
  // (lists every format), the DME values board and Mock Draft (own local format pickers),
  // and the Command Center (aggregates leagues that each keep their own format) — hide it there.
  const FORMAT_HIDDEN = ["/app", "/app/values", "/app/command", "/app/mock-draft", "/app/portfolio", "/app/pick-arbitrage", "/app/ledger", "/app/devy", "/app/advisor"];
  const hideFormat = /^\/app\/players\/[^/]+$/.test(location.pathname) || FORMAT_HIDDEN.includes(location.pathname);

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 border-b border-[var(--border)] flex items-center justify-between gap-3 px-4 sm:px-6 md:px-8 shrink-0">
          <Link to="/app/home" className="md:hidden inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:text-[var(--accent)] transition-colors" data-testid="mobile-menu-btn">
            <LayoutGrid className="w-5 h-5 text-[var(--accent)]" /> Menu
          </Link>
          {!hideFormat && <div className="text-[10px] tracking-widest text-[var(--text-3)] hidden sm:block">VALUE FORMAT</div>}
          <div className="flex items-center gap-2 sm:gap-3 ml-auto min-w-0">
            {!hideFormat && <FormatToggle />}
            <ThemeToggle />
            <MasterRefresh />
            {isPro && <NotificationBell />}
          </div>
        </header>
        <main className="flex-1 min-w-0 p-4 sm:p-6 md:p-8">
          <Outlet />
        </main>
        {isPro && <TradeTicker />}
      </div>
    </div>
  );
}
